import React, { Fragment, useContext, useEffect, useState } from "react";
import { Card, Row, Col, Form, OverlayTrigger, Tooltip} from "react-bootstrap";
import { AuthContext } from "../../Auth/AuthProvider";
import { abpnClient } from "../../Services/AbpnClient";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import { LookupItem } from "../../Shared/Models/LookupItem";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { MedicalEducation } from "../Models/MedicalEducation";

interface IProps {
    medicalEducation: MedicalEducation;
    degreeType: string | null;
    runValidation: boolean;
    resetForm: boolean;
    isFormValid: boolean;
    updateMedicalEducation: (medicalEducation: MedicalEducation) => void;
    updateDegreeType: (degreeType: string) => void;
    updateMedicalEducationValidation: (isValid: boolean) => void;
}

export function MedicalEducationCard(props: IProps) {
    const authContext = useContext(AuthContext);
    const degreeTypes: Array<string> = ["DO", "MD", "MBBS", "MBBCh", "MB", "MC"];
    const [medicalEducationLocationLookupList, setMedicalEducationLocationLookupList] = useState<Array<LookupItem>>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const getFormLookupItems = () => {
            abpnClient.get<Array<LookupItem>>(`/api/training/GetMedicalEducationLocationLookupList`, authContext)
                .then(result => {
                    setMedicalEducationLocationLookupList(result);
                    setLoading(false);
                });
        }
        getFormLookupItems();
    }, []);

    useEffect(() => {
        if (props.runValidation) validate();
    }, [props.runValidation]);

    useEffect(() => {
        if (props.resetForm) resetForm();
    }, [props.resetForm]);

    const updateLocationId = (e: React.ChangeEvent<HTMLSelectElement>) => {
        var updated = deepCopy(props?.medicalEducation);
        updated.locationId = e.target.value ? medicalEducationLocationLookupList?.find(_ => _.id === parseInt(e.target.value)).id : null;
        props.updateMedicalEducation(updated);
    }

    const updateDegreeType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        props.updateDegreeType(e.target.value);
    }

    const updateGraduationYear = (e: React.ChangeEvent<HTMLSelectElement>) => {
        var updated = deepCopy(props?.medicalEducation);
        updated.graduationYear = parseInt(e.target.value);
        props.updateMedicalEducation(updated);
    }

    const validate = () => {
        const form = document.getElementById("medicalSchoolForm") as HTMLFormElement;
        props.updateMedicalEducationValidation(form.checkValidity());
    }

    const resetForm = () => {
        const form = document.getElementById("medicalSchoolForm") as HTMLFormElement;
        form.reset();
    }

    const getMaxYear = () => {
        let currentDate = new Date();
        return currentDate.getFullYear();
    }

    const getLocationId = () => {
        return props.medicalEducation?.locationId ?? "";
    }

    const getFormattedDegreeType = (degreeType: string) => {
        if (!degreeType) {
            return "";
        } 
        return degreeType.replace(/[^a-zA-Z]+/g, '');
    }

    if (loading) return (<AbpnSpinner />)

    return (
        <Fragment>
            <Card.Title className="mb-0 fw-bolder">
                <span className="text-primary">Medical School</span>
                <OverlayTrigger placement={"right"} overlay={<Tooltip id="medicalSchoolInfo" bsPrefix="pc-tooltip">If resident attended more than one medical school, enter the one from which they graduated and were conferred their degree.</Tooltip>}>
                    <i className="las la-info-circle ml-1 text-pc-secondary"></i>
                </OverlayTrigger>
            </Card.Title>
            <Row className="mt-3">
                <Col xs={12} md={12} lg={12} xl={12}>
                    <Form noValidate id="medicalSchoolForm" validated={props.isFormValid === false}>
                        <Row className="align-items-end">
                            <Col md={3}>
                                <Form.Group controlId="graduationYear">
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Graduation Year<span className="text-primary ml-2">*</span></Form.Label>
                                    <Form.Control 
                                        defaultValue={props.medicalEducation?.graduationYear > 1900 ? props.medicalEducation.graduationYear : ""}
                                        required
                                        type="number" 
                                        tabIndex={7}
                                        placeholder="YYYY" 
                                        min={1900} 
                                        max={getMaxYear()} 
                                        onChange={(e: any) => {updateGraduationYear(e)}}>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={{span: 3, offset: 1}}>
                                <Form.Group controlId="medicalSchoolLocationId">
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Location<span className="text-primary ml-2">*</span></Form.Label>
                                        <Form.Select as="select"
                                            required
                                            tabIndex={8}
                                            onChange={(e: any) => { updateLocationId(e)}}
                                            value={getLocationId()}>
                                            <option value="">Select Location...</option>
                                            {medicalEducationLocationLookupList?.map((location, index) => (<option value={location.id} key={index}>{location.name}</option>))}
                                        </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={{span: 3, offset: 1}}>
                                <Form.Group controlId="degreeType">
                                    <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">Degree Type<span className="text-primary ml-2">*</span></Form.Label>
                                        <Form.Select as="select"
                                            required
                                            tabIndex={9}
                                            onChange={(e: any) => { updateDegreeType(e)}}
                                            value={getFormattedDegreeType(props.degreeType)}>
                                            <option value="">Select Degree Type...</option>
                                            {degreeTypes.map((degree, index) => <option value={degree} key={index}>{degree}</option>)}
                                        </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Fragment>
    );
}

export default MedicalEducationCard;
